import type { Notification } from "@pimo/notifications-plugin-v2";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

type NotificationTemplate = {
  title: string;
  textPattern: RegExp;
  parameterName: "projectName" | "dueDate";
};

// Trigger-generated notifications currently reach the frontend without their
// trigger id. Match only the unchanged built-in copy so custom notifications
// are never mistaken for translatable system messages.
const NOTIFICATION_TEMPLATES: Record<string, NotificationTemplate> = {
  TwoDayDeadlineReminder: {
    title: "The reporting period ends in two days.",
    textPattern:
      /^The current reporting period ends in two days\. Please update the project "(.+)"\.$/,
    parameterName: "projectName",
  },
  OneDayDeadlineReminder: {
    title: "The reporting period ends in one day.",
    textPattern:
      /^The current reporting period ends in one day\. Please update the project "(.+)"\.$/,
    parameterName: "projectName",
  },
  TodayDeadlineReminder: {
    title: "The reporting period ends today.",
    textPattern:
      /^The current reporting period ends today\. Please update the project "(.+)"\.$/,
    parameterName: "projectName",
  },
  NewReportingCycleStarted: {
    title: "A new reporting period has started today.",
    textPattern:
      /^A new reporting period has started today\. This new period ends on "(.+)"$/,
    parameterName: "dueDate",
  },
  ReportSubmitted: {
    title: "The project report has been submitted.",
    textPattern: /^The report of project "(.+)" has been submitted\.$/,
    parameterName: "projectName",
  },
};

const getLocalizedValue = (
  language: string,
  key: string,
  fallback: string,
  params?: Record<string, string>
) => {
  const localizedValue = getLocalizedMessage(language, key, params);
  return localizedValue && localizedValue !== key ? localizedValue : fallback;
};

export const getLocalizedNotificationCopy = (
  notification: Pick<Notification, "title" | "text">,
  language: string
) => {
  const templateEntry = Object.entries(NOTIFICATION_TEMPLATES).find(
    ([, template]) => template.title === notification.title
  );

  if (!templateEntry) {
    return { title: notification.title, text: notification.text };
  }

  const [templateKey, template] = templateEntry;
  const translationKey = `notificationMessages.${templateKey}`;
  const textMatch = notification.text.match(template.textPattern);

  return {
    title: getLocalizedValue(
      language,
      `${translationKey}.title`,
      notification.title
    ),
    text: textMatch
      ? getLocalizedValue(
          language,
          `${translationKey}.text`,
          notification.text,
          { [template.parameterName]: textMatch[1] }
        )
      : notification.text,
  };
};
